/**
 * Flussonic MSE Player Error Tracker
 *
 * Purpose: Prevents Sentry quota exhaustion from recursive Flussonic player errors.
 *
 * Problem: When changing audio/video sources in the Flussonic MSE player, the library
 * sometimes enters an invalid state where:
 * 1. The SourceBuffer gets detached from MediaSource during source switching
 * 2. Internal player code (checkVideoProgress) attempts to read 'buffered' property on removed SourceBuffer
 * 3. This triggers InvalidStateError which the library tries to handle internally
 * 4. The error handler itself fails with "Cannot use 'in' operator on undefined"
 * 5. This creates a recursive error loop generating 50K+ Sentry events per user
 *
 * Solution: Rate-limit these specific errors to prevent Sentry flooding while still
 * capturing the first few instances for debugging. The player eventually recovers or
 * can be reset, but we need to stop the error spam.
 *
 * Note: We cannot modify the library internals or switch libraries (proprietary mse_ld format),
 * so we filter at the Sentry integration level.
 */

import * as Sentry from "@sentry/react";

const MINUTE = 60 * 1000;

interface ErrorFrame {
  filename?: string;
  abs_path?: string;
  function?: string;
}

interface ErrorCheckResult {
  isFlussonicError: boolean;
  errorType?: string;
  errorMessage?: string;
}

/**
 * Checks if an error is from the Flussonic MSE player
 */
export function isFlussonicMSEError(
  error: Error | ErrorEvent | unknown
): ErrorCheckResult {
  let errorMessage = "";
  let errorType = "";
  let stackTrace: ErrorFrame[] = [];

  // Handle different error types
  if (error instanceof ErrorEvent) {
    errorMessage = error.message;
    errorType = error.error?.name || "Error";
    // Try to get stack from the error object
    if (error.error?.stack) {
      stackTrace = parseStackTrace(error.error.stack);
    }
  } else if (error instanceof Error) {
    errorMessage = error.message;
    errorType = error.name;
    if (error.stack) {
      stackTrace = parseStackTrace(error.stack);
    }
  } else if (typeof error === "string") {
    errorMessage = error;
    errorType = "Error";
  }

  // Check for Flussonic SourceBuffer errors
  const isSourceBufferError =
    errorType === "InvalidStateError" &&
    (errorMessage.includes("SourceBuffer") ||
      errorMessage.includes("buffered") ||
      errorMessage.includes("not, or is no longer, usable"));

  // Check for the "in operator" error with undefined (secondary error from error handler)
  const isInOperatorError =
    errorType === "TypeError" &&
    errorMessage.includes("Cannot use 'in' operator") &&
    errorMessage.includes("undefined");

  // Check if error comes from Flussonic player
  const isFlussonicStack = stackTrace.some(
    (frame) =>
      frame.filename?.includes("FlussonicMsePlayer") ||
      frame.filename?.includes("flussonic") ||
      frame.abs_path?.includes("FlussonicMsePlayer") ||
      frame.filename?.includes("@flussonic")
  );

  // Check for the specific problematic methods
  const isCheckVideoProgress = stackTrace.some((frame) =>
    frame.function?.includes("checkVideoProgress")
  );

  const isFlussonicError =
    (isSourceBufferError && (isFlussonicStack || isCheckVideoProgress)) ||
    (isInOperatorError && isFlussonicStack);

  return {
    isFlussonicError,
    errorType,
    errorMessage,
  };
}

/**
 * Simple stack trace parser
 */
function parseStackTrace(stack: string): ErrorFrame[] {
  const frames: ErrorFrame[] = [];
  const lines = stack.split("\n");

  for (const line of lines) {
    // Match common stack trace formats
    const match =
      line.match(/at\s+(.+?)\s+\((.+?)\)/) || line.match(/(.+?)@(.+)/);
    if (match) {
      frames.push({
        function: match[1]?.trim(),
        filename: match[2]?.trim(),
        abs_path: match[2]?.trim(),
      });
    }
  }

  return frames;
}

/**
 * Detects the known Flussonic WebRTC-signaling library bug: a TypeError thrown
 * when the library calls `.send()` on a WebSocket reference that was already
 * nulled during signaling/teardown (Sentry NEWSQUAWK-PORTALS-CX, observed for
 * bnolan@genevatrading.com immediately after an MSE codec reset). It is
 * harmless to the stream itself, but as an *uncaught* synchronous throw it
 * reaches `window.onerror` and can trigger app-shell crash recovery / a Sentry
 * app-crash and interrupt the disconnect→reconnect cycle the MSE self-heal
 * relies on. The global handlers must therefore `preventDefault()` it.
 *
 * Signature match is message-based (TypeError reading `send` on `null`):
 * `error.stack` at `window.onerror` time is the minified production bundle, so
 * a "flussonic" filename/function frame is usually NOT present at runtime
 * (unlike the Sentry-side `beforeSend`, which sees symbolicated frames). A
 * flussonic stack frame, when present, is treated as corroborating signal for
 * logging only — not required — so this stays effective in production.
 */
export function isFlussonicWebRtcNullSend(
  error: Error | ErrorEvent | unknown
): boolean {
  let message = "";
  let type = "";

  if (error instanceof ErrorEvent) {
    message = error.message || error.error?.message || "";
    type = error.error?.name || "Error";
  } else if (error instanceof Error) {
    message = error.message || "";
    type = error.name;
  } else {
    return false;
  }

  return (
    type === "TypeError" &&
    message.includes("send") &&
    message.includes("null")
  );
}

export const flussonicErrorTracker = {
  counts: new Map<string, number>(),
  maxPerError: 5, // Allow first 5 of each unique error
  maxTotal: 50, // Max 50 Flussonic errors per session total
  totalCount: 0,
  resetInterval: 30 * MINUTE, // Reset counters after 30 minutes

  /**
   * Determines if an error should be blocked from Sentry
   * @param {string} errorSignature - Unique identifier for the error
   * @returns {boolean} - True if error should be blocked
   */
  shouldBlock(errorSignature: string): boolean {
    const count = this.counts.get(errorSignature) || 0;

    if (count >= this.maxPerError || this.totalCount >= this.maxTotal) {
      return true;
    }

    this.counts.set(errorSignature, count + 1);
    this.totalCount++;

    // Auto-reset after interval
    if (count === 0) {
      setTimeout(() => {
        const current = this.counts.get(errorSignature) || 0;
        this.counts.set(errorSignature, Math.max(0, current - 1));
        this.totalCount = Math.max(0, this.totalCount - 1);
      }, this.resetInterval);
    }

    return false;
  },

  /**
   * Checks if error is related to Flussonic player issues
   * @param {Object} event - Sentry event object
   * @returns {Object|null} - Returns modified event or null to block
   */
  beforeSend(event: Sentry.Event) {
    const eventType = event.exception?.values?.[0]?.type || "";
    const eventMessage = event.exception?.values?.[0]?.value || "";
    const stackFrames = event.exception?.values?.[0]?.stacktrace?.frames || [];

    // Filter "[object Object]" errors — these provide zero diagnostic value
    // (Safety net for any remaining bad serialization paths)
    if (eventMessage === "[object Object]") {
      console.warn("[Blocked from Sentry] useless [object Object] error");
      return null;
    }

    // Filter AbortError from play() interruption — expected during reconnection.
    // Some browsers serialize DOMException with name "AbortError" as type "DOMException"
    // rather than "AbortError", so we check both type variants plus a message-only fallback.
    if (
      (eventType === "AbortError" || eventType === "DOMException") &&
      eventMessage.includes("play()")
    ) {
      return null;
    }
    if (
      eventMessage.includes("play() request was interrupted") ||
      eventMessage.includes("interrupted by a call to pause")
    ) {
      return null;
    }
    if (eventMessage.includes("play_promise_reject")) {
      return null;
    }

    // Filter QuotaExceededError from SourceBuffer overflow (MSE long sessions)
    if (eventType === "QuotaExceededError") {
      const sig = "mse_buffer_quota";
      if (this.shouldBlock(sig)) {
        console.warn("[Blocked from Sentry] QuotaExceededError (buffer overflow)");
        return null;
      }
      event.fingerprint = ["flussonic-buffer-error", eventType];
      event.tags = {
        ...event.tags,
        "flussonic.error": true,
        "flussonic.error_type": "buffer_overflow",
      };
    }

    // Filter null .send() crashes from Flussonic WebRTC signaling (library bug)
    if (
      eventType === "TypeError" &&
      eventMessage.includes("send") &&
      eventMessage.includes("null")
    ) {
      const hasFlussonicFrame = stackFrames.some(
        (frame) =>
          frame.filename?.includes("flussonic") ||
          frame.filename?.includes("@flussonic") ||
          frame.function?.includes("sendWsMsg") ||
          frame.function?.includes("waitForConnection")
      );
      if (hasFlussonicFrame) {
        const sig = "webrtc_null_send";
        if (this.shouldBlock(sig)) {
          console.warn("[Blocked from Sentry - Flussonic null .send()]", eventMessage);
          return null;
        }
        event.fingerprint = ["flussonic-webrtc-null-send"];
        event.tags = { ...event.tags, "flussonic.error": true, "flussonic.error_type": "null_send" };
      }
    }

    // Existing Flussonic MSE error detection and rate-limiting
    const mockError = {
      name: eventType,
      message: eventMessage,
      stack: stackFrames
        .map(
          (frame) =>
            `at ${frame.function || "unknown"} (${frame.filename || "unknown"})`
        )
        .join("\n"),
    } as Error;

    const { isFlussonicError, errorType, errorMessage } =
      isFlussonicMSEError(mockError);

    // Only process Flussonic-related errors
    if (isFlussonicError && errorType && errorMessage) {
      const errorSignature = `${errorType}_${errorMessage.substring(0, 50)}`;

      // Check if we should block this error
      if (this.shouldBlock(errorSignature)) {
        // Log to console for debugging but don't send to Sentry
        console.warn(
          "[Blocked from Sentry - Flussonic Error]",
          errorType,
          errorMessage,
          `(Count: ${this.counts.get(errorSignature)}/${this.maxPerError}, Total: ${this.totalCount}/${this.maxTotal})`
        );
        return null; // Block from Sentry
      }

      // Add context to the errors we do send
      event.fingerprint = ["flussonic-player-error", errorType];
      event.tags = {
        ...event.tags,
        "flussonic.error": true,
        "flussonic.error_type": errorType.includes("SourceBuffer")
          ? "sourcebuffer"
          : "in_operator",
        "flussonic.count": this.counts.get(errorSignature) || 0,
      };

      // Add breadcrumb for context
      event.breadcrumbs = event.breadcrumbs || [];
      event.breadcrumbs.push({
        type: "info",
        category: "flussonic",
        message: "Flussonic player error detected (rate-limited)",
        level: "warning",
        data: {
          errorCount: this.counts.get(errorSignature),
          totalFlussonicErrors: this.totalCount,
        },
      });
    }

    return event;
  },

  reset() {
    this.counts.clear();
    this.totalCount = 0;
  },
};

/**
 * Global error handler for unhandled Flussonic MSE errors
 * Prevents recursive error loops from crashing the app or flooding Sentry
 */
export function handleGlobalFlussonicError(
  event: ErrorEvent | Event
): boolean | void {
  // Only handle ErrorEvent
  if (!(event instanceof ErrorEvent)) {
    return;
  }

  // Known Flussonic WebRTC-signaling library bug (null .send()). Swallow it so
  // an uncaught synchronous throw can't trip app-shell crash recovery or break
  // the MSE disconnect→reconnect self-heal. NOT routed through shouldBlock: the
  // Sentry-side beforeSend already governs issue volume; the global handler's
  // sole job here is to stop the crash reaction.
  if (isFlussonicWebRtcNullSend(event)) {
    console.warn(
      "[Suppressed - Flussonic WebRTC null .send()]",
      event.message,
      "(known library bug — preventing app-crash reaction)"
    );
    event.preventDefault();
    return false;
  }

  const { isFlussonicError, errorType, errorMessage } =
    isFlussonicMSEError(event);

  if (isFlussonicError && errorType && errorMessage) {
    const errorSignature = `${errorType}_${errorMessage.substring(0, 50)}`;

    // Check if we should block this error
    if (flussonicErrorTracker.shouldBlock(errorSignature)) {
      // Block the error from propagating
      console.warn(
        "[Blocked - Global Flussonic Error]",
        errorType,
        errorMessage,
        `(Count: ${flussonicErrorTracker.counts.get(errorSignature)}/${flussonicErrorTracker.maxPerError}, Total: ${flussonicErrorTracker.totalCount}/${flussonicErrorTracker.maxTotal})`
      );

      // Prevent default error handling
      event.preventDefault();
      return false;
    }

    // Log first few occurrences
    console.warn(
      "[Flussonic MSE Error - Tracked]",
      errorType,
      errorMessage,
      `(Count: ${flussonicErrorTracker.counts.get(errorSignature) || 1}/${flussonicErrorTracker.maxPerError})`
    );
  }
}

/**
 * Global handler for unhandled promise rejections from Flussonic player teardown.
 *
 * During player destroy/stop, internal promises can reject with undefined
 * (the tail end of the error cascade). Additionally, play() AbortError rejections
 * can surface as unhandled when the catch handler isn't reached in time.
 */
export function handleGlobalFlussonicUnhandledRejection(
  event: PromiseRejectionEvent
): void {
  const reason = event.reason;

  // Rejection with undefined/null — typically from player.destroy() internal promise chain
  if (reason === undefined || reason === null) {
    console.debug("[Flussonic] Suppressed unhandled rejection with undefined/null reason (likely teardown)");
    event.preventDefault();
    return;
  }

  // AbortError from play() interruption during reconnection
  if (reason instanceof DOMException && reason.name === "AbortError") {
    console.debug("[Flussonic] Suppressed AbortError unhandled rejection (play() interrupted)");
    event.preventDefault();
    return;
  }

  // Known Flussonic WebRTC-signaling null .send() bug surfacing as a rejected
  // promise rather than a synchronous throw. Same rationale as the global
  // error handler — swallow so it can't wedge MSE recovery.
  if (isFlussonicWebRtcNullSend(reason)) {
    console.warn(
      "[Suppressed - Flussonic WebRTC null .send() rejection]",
      reason instanceof Error ? reason.message : String(reason)
    );
    event.preventDefault();
    return;
  }

  // Known Flussonic error patterns in rejected promises
  if (reason instanceof Error) {
    const { isFlussonicError, errorType, errorMessage } = isFlussonicMSEError(reason);
    if (isFlussonicError && errorType && errorMessage) {
      const errorSignature = `rejection_${errorType}_${errorMessage.substring(0, 50)}`;
      if (flussonicErrorTracker.shouldBlock(errorSignature)) {
        console.warn("[Blocked - Flussonic Unhandled Rejection]", errorType, errorMessage);
        event.preventDefault();
        return;
      }
    }
  }
}
